import React, { useState, useEffect } from 'react';
import { Play, Pause, CheckCircle, Minimize, Maximize, Clock, ShieldCheck, Info, RefreshCw, Zap, Trash2, MessageSquare, Plus, Search, X, Heart, Bluetooth } from 'lucide-react';

import { parseRestTime, isBarbellExercise } from '../utils/workoutUtils';
import SetRow from './Workout/SetRow';
import TimerControls from './Workout/TimerControls';
import WorkoutModals from './Workout/WorkoutModals';
import { useHeartRate } from '../hooks/useHeartRate';

import { LOCAL_EXERCISES } from '../data/exercises';
import { showToast } from '../utils/toast';

export default function WorkoutFocus({
    user, currentWod, liveExercises, recordedTimes, setRecordedTimes,
    activeTimerTask, setActiveTimerTask, timerSeconds, setTimerSeconds,
    timerRunning, setTimerRunning, formatTime, toggleFullScreen, isFullscreen,
    intervalPhase, restTime, setRestTime, time, setTime, isRunning, setIsRunning,
    soundEnabled, setSoundEnabled, rounds, setRounds, currentIntervalRound,
    skipToNextPhase, groups, history, setPrModal, loggedPRs,
    cleanupFocusMode, setView, showConfirmModal, setShowConfirmModal,
    handleLogWorkout, prModal, setSwapModal, prs, exerciseDatabase
}) {
    const isShredding = user?.training_type?.toLowerCase().includes('shredding');
    const displayFormat = isShredding && currentWod?.format === 'Krachttraining' ? 'SHREDDING' : currentWod?.format;

    const hr = useHeartRate(user?.age || 30);

    const validGroupsRef = React.useRef(groups);
    const isValidGroup = groups && ((groups.Warmup && groups.Warmup.length > 0) || (groups.Training && Object.keys(groups.Training).length > 0) || (groups.Cardio && groups.Cardio.length > 0));
    if (isValidGroup) validGroupsRef.current = groups;
    const displayGroups = isValidGroup ? groups : (validGroupsRef.current || groups);

    const hasTraining = displayGroups?.Training && Object.keys(displayGroups.Training).length > 0;
    const hasWarmup = displayGroups?.Warmup && displayGroups.Warmup.length > 0;
    const hasCardio = displayGroups?.Cardio && displayGroups.Cardio.length > 0;
    const isStructuredFitness = currentWod?.isFitness && (hasTraining || hasWarmup || hasCardio);

    const [setTracking, setSetTracking] = useState({});
    const [unitPreferences, setUnitPreferences] = useState({});
    const [infoOpen, setInfoOpen] = useState({});
    const [rpeOpen, setRpeOpen] = useState({});
    const [ignoredExercises, setIgnoredExercises] = useState([]);
    const [noteOpen, setNoteOpen] = useState({});
    const [exerciseNotes, setExerciseNotes] = useState({});

    const [focusStartedAt, setFocusStartedAt] = useState(new Date().toISOString());
    const [weightModal, setWeightModal] = useState({ isOpen: false, key: null, sIdx: null, weight: 0, isBarbell: false, barWeight: 20 });
    const [addExModal, setAddExModal] = useState(false);
    const [addExSearch, setAddExSearch] = useState('');

    const handleAddCustomExercise = (exName) => {
        const trackingKey = 'custom_' + Date.now();
        const newEx = {
            name: exName, cleanName: exName, sets: 3, reps: 10, weight: 0,
            originalIdx: trackingKey, displayCode: '+', isCustom: true
        };

        if (groups && !groups.Training) groups.Training = { 'Extra': [] };
        else if (groups && !groups.Training['Extra']) groups.Training['Extra'] = [];

        if (groups && groups.Training) groups.Training['Extra'].push(newEx);
        if (liveExercises) liveExercises.push(newEx);

        setSetTracking(prev => ({
            ...prev,
            [trackingKey]: Array.from({ length: 3 }).map(() => ({
                reps: 10, weight: 0, type: 'normal', done: false, exName: exName
            }))
        }));
        setUnitPreferences(prev => ({ ...prev, [trackingKey]: 'KG' }));
        setAddExModal(false);
        setAddExSearch('');
    };
    const [manualDuration, setManualDuration] = useState(null);
    const [showTimePrompt, setShowTimePrompt] = useState(false);
    const [timeInput, setTimeInput] = useState("60");

    const markAllSetsCompleted = () => {
        setSetTracking(prev => {
            const updated = {};
            Object.entries(prev).forEach(([k, setsArr]) => {
                if (Array.isArray(setsArr)) {
                    updated[k] = setsArr.map(s => ({ ...s, done: true }));
                } else {
                    updated[k] = setsArr;
                }
            });
            return updated;
        });
    };

    useEffect(() => {
        const nowIso = new Date().toISOString();
        setFocusStartedAt(nowIso);
        localStorage.setItem('focusStartedAt', String(Date.now()));
    }, [currentWod]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        const wType = currentWod?.type || currentWod?.format || 'Workout';
        fetch('/api/workouts/live/start', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ type: wType })
        }).catch(err => console.log('Live status update failed:', err));

        return () => {
            const tokenToUse = localStorage.getItem('token');
            if (!tokenToUse) return;
            const url = '/api/workouts/live/stop';
            const headers = new Headers({ 'Authorization': `Bearer ${tokenToUse}` });
            try { fetch(url, { method: 'POST', headers }).catch(()=>{}); } catch (e) { navigator.sendBeacon(url); }
        };
    }, [currentWod]);

    useEffect(() => {
        let wakeLock = null;
        const requestWakeLock = async () => { try { if ('wakeLock' in navigator) { wakeLock = await navigator.wakeLock.request('screen'); } } catch (err) {} };
        requestWakeLock();
        const handleVisibilityChange = () => { if (document.visibilityState === 'visible') requestWakeLock(); };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => { document.removeEventListener('visibilitychange', handleVisibilityChange); if (wakeLock) wakeLock.release(); };
    }, []);

    useEffect(() => {
        if (!isStructuredFitness || !groups?.Training) return;

        setSetTracking(prev => {
            let baseState = { ...prev };
            if (Object.keys(baseState).length === 0) {
                const savedTracking = localStorage.getItem('active_workout_tracking');
                if (savedTracking) {
                    try {
                        const parsed = JSON.parse(savedTracking);
                        if (Object.keys(parsed).length > 0) baseState = parsed;
                    } catch(e) {}
                }
            }

            let hasChanges = false;
            const newState = { ...baseState };
            const newUnits = { ...unitPreferences };
            const newNotes = { ...exerciseNotes };
            const isCustomBuiltWod = String(currentWod?.id || '').startsWith('custom_');

            Object.values(groups.Training).forEach(exercisesArray => {
                exercisesArray.forEach(ex => {
                    const key = ex.originalIdx !== undefined ? ex.originalIdx : ex.name;
                    const safeName = ex.cleanName || ex.name || '';
                    const isDeloadEx = currentWod?.isDeload || ex.strategy === 'deload';

                    if (ex.note && !newNotes[key]) newNotes[key] = ex.note;

                    const currentSets = newState[key];

                    let targetWeight = (ex.weight !== undefined && ex.weight !== null && ex.weight !== '') ? parseFloat(ex.weight) || 0 : 0;
                    const hasExplicitBuilderWeight = (isCustomBuiltWod || ex.customWeight) && targetWeight > 0;
                    let histUnit = 'KG';
                    let foundHist = false;

                    if (!hasExplicitBuilderWeight && history && history.length > 0) {
                        for (const wod of history) {
                            if ((wod.type || '').toLowerCase().includes('deload')) continue;
                            let wodDataArr = wod.workout_data;
                            if (typeof wodDataArr === 'string') {
                                try { wodDataArr = JSON.parse(wodDataArr); } catch (e) { wodDataArr = null; }
                            }
                            if (Array.isArray(wodDataArr) && wodDataArr.length > 0) {
                                const exMatch = wodDataArr.find(e => e && e.exercise && e.exercise.toLowerCase() === safeName.toLowerCase());
                                if (exMatch && Array.isArray(exMatch.sets) && exMatch.sets.length > 0) {
                                    targetWeight = Math.max(...exMatch.sets.map(s => parseFloat(s.weight) || 0));
                                    if (targetWeight > 0) {
                                        foundHist = true;
                                        break;
                                    }
                                }
                            } else if (wod.description) {
                                const safeRegexName = safeName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                                const regex = new RegExp(safeRegexName + ':\\s*((?:(?:\\(W\\)\\s*)?\\d+x[\\d.]+(?:kg|lbs)?(?:,\\s*)?)+)', 'i');
                                const match = wod.description.match(regex);
                                if (match) {
                                    const weightMatches = [...match[1].matchAll(/x([\d.]+)/g)];
                                    if (weightMatches.length > 0) {
                                        targetWeight = Math.max(...weightMatches.map(m => parseFloat(m[1])));
                                        foundHist = true;
                                    }
                                    if (match[1].toLowerCase().includes('lbs')) histUnit = 'LBS';
                                    if (targetWeight > 0) break;
                                }
                            }
                        }
                    }

                    if (!hasExplicitBuilderWeight && !foundHist && prs && prs.length > 0) {
                        const prMatch = prs.find(p => p.exercise && p.exercise.toLowerCase() === safeName.toLowerCase());
                        if (prMatch) { targetWeight = parseFloat(prMatch.weight) || 0; }
                    }

                    if (!hasExplicitBuilderWeight && foundHist && isDeloadEx && targetWeight > 0) {
                        const step = ex.step || (safeName.toLowerCase().includes('db') || safeName.toLowerCase().includes('dumbbell') ? 2 : 2.5);
                        targetWeight = Math.max(step, Math.round((targetWeight * 0.65) / step) * step);
                    }

                    if (!currentSets || currentSets.length === 0) {
                        const rawRepsStr = String(ex.reps || '10').trim();
                        const isSchemeList = /^\d+(-\d+){1,6}$/.test(rawRepsStr) && rawRepsStr.split('-').length !== 2;
                        const schemeParts = isSchemeList ? rawRepsStr.split('-').map(n => parseInt(n, 10) || 10) : null;

                        const numSets = schemeParts ? schemeParts.length : (parseInt(ex.sets, 10) || 4);
                        let startReps = ex.reps;
                        if (typeof startReps === 'string' && startReps.includes('-')) startReps = parseInt(startReps.split('-')[0], 10) || 10;
                        else startReps = parseInt(startReps, 10) || 10;

                        if (!newUnits[key]) newUnits[key] = ex.unit || histUnit || 'KG';

                        newState[key] = Array.from({ length: numSets }).map((_, idx) => ({
                            reps: schemeParts ? schemeParts[idx] : startReps,
                            weight: targetWeight,
                            type: ex.isFailure ? 'failure' : 'normal',
                            done: false,
                            exName: safeName
                        }));
                        hasChanges = true;
                    }
                    else if (currentSets[0] && currentSets[0].exName && currentSets[0].exName !== safeName) {
                        newState[key] = currentSets.map(s => ({ ...s, weight: targetWeight, done: false, exName: safeName }));
                        hasChanges = true;
                    }
                    else if (currentSets[0] && !currentSets[0].exName) {
                        newState[key] = currentSets.map(s => ({ ...s, exName: safeName }));
                        hasChanges = true;
                    }
                });
            });

            if (Object.keys(newUnits).length > 0) setUnitPreferences(newUnits);
            if (Object.keys(newNotes).length > 0) setExerciseNotes(newNotes);

            return hasChanges ? newState : prev;
        });
    }, [currentWod, groups, history, prs]);

    useEffect(() => { if (Object.keys(setTracking).length > 0) { localStorage.setItem('active_workout_tracking', JSON.stringify(setTracking)); } }, [setTracking]);

    // Werkt nu altijd — óók als sets al groen aangevinkt zijn (bijv. na Achteraf Gelogd)!
    const updateSetData = (key, setIndex, field, value) => {
        setSetTracking(prev => {
            if (!prev[key]) return prev;
            const updated = { ...prev };
            const sets = [...updated[key]];
            const previousVal = sets[setIndex]?.[field];

            let newValue;
            if (value === '') {
                newValue = '';
            } else if (field === 'weight') {
                const parsed = parseFloat(String(value).replace(',', '.'));
                newValue = isNaN(parsed) ? 0 : Math.max(0, parsed);
            } else if (field === 'rpe') {
                const parsed = parseFloat(String(value).replace(',', '.'));
                newValue = isNaN(parsed) ? '' : Math.min(10, Math.max(1, parsed));
            } else {
                const parsed = parseInt(value, 10);
                newValue = isNaN(parsed) ? 0 : Math.max(0, parsed);
            }

            sets[setIndex] = { ...sets[setIndex], [field]: newValue };

            // Cascadeer automatisch naar volgende sets als die nog niet voltooid zijn ÓF nog op exact dezelfde oude waarde stonden
            if (field === 'weight' || field === 'reps') {
                for (let i = setIndex + 1; i < sets.length; i++) {
                    if (!sets[i].done || sets[i][field] === previousVal) {
                        sets[i] = { ...sets[i], [field]: newValue };
                    }
                }
            }

            updated[key] = sets;
            return updated;
        });
    };

    const cycleSetType = (key, setIndex) => {
        setSetTracking(prev => {
            const updated = { ...prev }; const sets = [...updated[key]];
            const currentType = sets[setIndex].type || 'normal';
            const nextType = currentType === 'normal' ? 'warmup' : currentType === 'warmup' ? 'dropset' : currentType === 'dropset' ? 'failure' : 'normal';
            sets[setIndex] = { ...sets[setIndex], type: nextType };
            updated[key] = sets; return updated;
        });
    };

    const addSet = (key) => {
        setSetTracking(prev => {
            const updated = { ...prev }; const currentSets = updated[key] || [];
            if (currentSets.length === 0) return prev;
            const lastSet = currentSets[currentSets.length - 1];
            updated[key] = [...currentSets, { reps: lastSet.reps, weight: lastSet.weight, type: 'normal', done: lastSet.done, exName: lastSet.exName }];
            return updated;
        });
    };

    const removeLastSet = (key) => {
        setSetTracking(prev => {
            const updated = { ...prev }; const currentSets = updated[key] || [];
            if (currentSets.length <= 1) return prev;
            updated[key] = currentSets.slice(0, -1); return updated;
        });
    };

    const toggleSet = (key, setIndex, restAmount, isLastSetInBlock) => {
        const currentSets = setTracking[key];
        if (!currentSets || !currentSets[setIndex]) return;

        const isNowDone = !currentSets[setIndex].done;

        setSetTracking(prev => {
            const updated = { ...prev }; if (!updated[key]) return prev;
            const newSets = [...updated[key]]; newSets[setIndex] = { ...newSets[setIndex], done: isNowDone };
            updated[key] = newSets; return updated;
        });

        if (isNowDone) {
            if (isShredding && restAmount !== null && restAmount > 0) { setRestTime(parseInt(restAmount, 10)); }
            else if (!isShredding) {
                const getRestVal = (val, fallback) => { const parsed = parseInt(val, 10); return isNaN(parsed) ? fallback : parsed; };
                const userBlockRest = getRestVal(user?.rest_between_blocks, 120);
                const userSetRest = getRestVal(user?.rest_between_sets, 90);

                if (isLastSetInBlock) setRestTime(userBlockRest);
                else if (restAmount !== null && restAmount > 0) setRestTime(parseInt(restAmount, 10));
                else setRestTime(userSetRest);
            }

            setTimeout(() => {
                const allRows = Array.from(document.querySelectorAll('.workout-set-row'));
                const nextUndone = allRows.find(row => !row.classList.contains('set-done'));
                if (nextUndone) { const y = nextUndone.getBoundingClientRect().top + window.pageYOffset - 160; window.scrollTo({ top: y, behavior: 'smooth' }); }
            }, 300);
        }
    };

    const toggleUnit = (key) => setUnitPreferences(prev => ({ ...prev, [key]: prev[key] === 'LBS' ? 'KG' : 'LBS' }));
    const toggleInfo = (key) => setInfoOpen(prev => ({ ...prev, [key]: !prev[key] }));
    const toggleNote = (key) => setNoteOpen(prev => ({ ...prev, [key]: !prev[key] }));
    const removeExerciseCompletely = (key) => {
        if(window.confirm("Wil je deze oefening volledig verwijderen? Hij wordt dan overgeslagen en NIET in je historie opgeslagen.")) {
            setIgnoredExercises(prev => [...prev, key]);
        }
    };

    const toggleRpe = (key) => setRpeOpen(prev => ({ ...prev, [key]: !prev[key] }));
    const updateNote = (key, text) => setExerciseNotes(prev => ({ ...prev, [key]: text }));

    const getExerciseTip = (ex) => {
        const ext = (v) => {
            if (!v) return '';
            if (typeof v === 'string') return v;
            if (typeof v === 'object') return v.tip || v.desc || v.description || v.name || '';
            return String(v);
        };
        const tip = ext(ex.tip) || ext(ex.description) || ext(ex.desc);
        if (tip && tip.length > 4) return tip;
        const nameToSearch = String(ex.cleanName || ex.name || '').toLowerCase().trim();
        const localMatch = LOCAL_EXERCISES.find(localEx => {
            const t = String(localEx.title || '').toLowerCase().trim();
            const k = String(localEx.keyword || '').toLowerCase().trim();
            return (t && nameToSearch.includes(t)) || (k && nameToSearch.includes(k));
        });
        if (localMatch) return ext(localMatch.tip) || ext(localMatch.desc) || "Focus op een veilige uitvoering.";
        return "Concentreer je op een veilige en correcte uitvoering.";
    };

    const logWorkoutWithDetails = async () => {
        let scoreText = ""; let finalDesc = currentWod.description || ''; let newLoggedPRs = []; let pendingSubmissions = new Set(); let structuredWorkoutData = [];

        if (isStructuredFitness && liveExercises.length > 0) {
            const anySetCheckedAtAll = Object.values(setTracking || {}).some(arr => Array.isArray(arr) && arr.some(s => s.done));

            finalDesc = liveExercises.filter(ex => !ignoredExercises.includes(ex.originalIdx !== undefined ? ex.originalIdx : ex.name)).map((ex, idx) => {
                const safeName = String(ex.cleanName || ex.name || 'Oefening');
                let cleanName = safeName;
                const matchName = safeName.match(/^([A-Z])(\d)?[\s\-:.)]+(.*)/i);
                let displayCode = ex.order ? String(ex.order).charAt(0).toUpperCase() : ex.displayCode ? String(ex.displayCode).charAt(0).toUpperCase() : matchName ? matchName[1].toUpperCase() : String.fromCharCode(65 + idx);
                if (matchName) cleanName = matchName[3].trim();

                if (ex.isCardioBlock) {
                    return `- ${displayCode} - ${cleanName}: ${ex.cardioTarget || `${ex.reps} ${ex.metric || 'Min'}`}`;
                }

                const trackingKey = ex.originalIdx !== undefined ? ex.originalIdx : idx;
                const sets = (setTracking && setTracking[trackingKey]) ? setTracking[trackingKey] : [];
                const completedSets = anySetCheckedAtAll ? sets.filter(s => s.done) : sets;

                structuredWorkoutData.push({
                    exercise: cleanName, targetSets: parseInt(ex.sets, 10) || sets.length, targetReps: ex.reps, weight: ex.weight || 0, strategy: ex.strategy || null,
                    sets: completedSets.map(s => ({ reps: parseInt(s.reps, 10) || 0, weight: parseFloat(s.weight) || 0, type: s.type, done: true }))
                });

                if (completedSets.length === 0) return `- ${displayCode ? displayCode + ' - ' : ''}0x0 ${cleanName} (Overgeslagen)`;

                const activeUnit = (unitPreferences[trackingKey] || 'KG').toLowerCase();
                const setsString = completedSets.map(s => {
                    let prefix = ''; if (s.type === 'warmup') prefix = '(W) '; if (s.type === 'dropset') prefix = '(D) '; if (s.type === 'failure') prefix = '(F) ';
                    return `${prefix}${s.reps || 0}x${s.weight || 0}${activeUnit}`;
                }).join(', ');

                let maxWeight = 0;
                completedSets.forEach(s => {
                    const parsedWeight = parseFloat(s.weight);
                    if(!isNaN(parsedWeight) && parsedWeight > maxWeight && s.type !== 'warmup') maxWeight = parsedWeight;
                });
                if(maxWeight > 0 && !currentWod?.isDeload) newLoggedPRs.push({ name: cleanName, weight: maxWeight, unit: activeUnit.toUpperCase() });

                let line = `- ${displayCode ? displayCode + ' - ' : ''}${cleanName}: ${setsString}`;
                if (exerciseNotes[trackingKey] && exerciseNotes[trackingKey].trim() !== '') line += `\n  📝 Notitie: ${exerciseNotes[trackingKey].trim()}`;
                return line;
            }).join('\n');
            const recordedStrs = Object.entries(recordedTimes).map(([k, v]) => `- Tijd ${k}: ${v}`); if (recordedStrs.length > 0) finalDesc += '\n\nGeklokte Tijden:\n' + recordedStrs.join('\n');
        }

        if (hr.avgBpm > 0) {
            finalDesc += `\n\n💓 Hartslag: Gem. ${hr.avgBpm} BPM | Max. ${hr.maxBpm} BPM`;
        }

        const typeStrUpper = String(currentWod?.type || '').toUpperCase();
        const isCardioScore = typeStrUpper.includes('CARDIO');

        const savedStart = localStorage.getItem('focusStartedAt');
        const elapsedSec = Math.max(0, Math.round((Date.now() - (savedStart ? parseInt(savedStart, 10) : new Date(focusStartedAt).getTime())) / 1000));
        // Begrens automatische duur op max 3 uur (10800s) zodat een vergeten tabblad nooit meer 20456 min logt
        const safeElapsed = elapsedSec > 10800 ? 3600 : elapsedSec;

        let workoutDurationSeconds = manualDuration !== null ? manualDuration : safeElapsed;
        if (isStructuredFitness && time > 0 && manualDuration === null) {
            workoutDurationSeconds = time;
        }

        if (!isCardioScore && currentWod.format === 'AMRAP') scoreText = `\nScore: ${rounds} Rondes`;
        else if (!isCardioScore && ['Tabata', 'EMOM'].includes(currentWod.format)) scoreText = `\nVoltooid: ${currentIntervalRound} Rondes`;
        else if (['For Time', 'Chipper', 'Hero', 'Endurance', 'Intervals', 'Medley'].includes(currentWod.format) || (currentWod.timerMode === 'stopwatch' && !isStructuredFitness) || isCardioScore) scoreText = `\nTijd: ${formatTime(time)}`;
        else if (currentWod.format === 'LISS' || currentWod.timerMode === 'countdown') scoreText = `\nVoltooid (Timer: ${formatTime(time)} resterend)`;
        else if (isStructuredFitness) scoreText = `\nVoltooid: Krachttraining (${formatTime(workoutDurationSeconds)})`;

        try {
            const parsedIntensity = parseInt(currentWod.intensity, 10);
            const finalType = currentWod.title || currentWod.type || 'Krachttraining';
            const payload = {
                type: finalType.replace('Historie: ', ''), intensity: isNaN(parsedIntensity) ? 5 : parsedIntensity,
                description: finalDesc + scoreText, loggedPRs: newLoggedPRs, workout_data: structuredWorkoutData,
                duration: workoutDurationSeconds, startedAt: focusStartedAt
            };
            if (navigator.onLine && pendingSubmissions.size > 0) {
                for (const customExName of Array.from(pendingSubmissions)) {
                    try { await fetch('/api/exercises/pending', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify({ exercise_name: customExName }) }); } catch(e) { }
                }
            }

            hr.disconnectHeartRate();
            handleLogWorkout(payload);
        } catch (err) { }
    };

    const btnBase = "flex items-center justify-center rounded-full shadow-2xl transition-transform hover:scale-105 shrink-0";
    const btnLarge = `${btnBase} w-20 h-20 sm:w-24 sm:h-24`;

    if (activeTimerTask) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-zinc-950 text-white focus-mode-container">
                <h2 className="text-3xl font-bold text-orange-500 mb-2">{activeTimerTask.cleanName || activeTimerTask.name}</h2>
                <h1 className="text-[25vw] sm:text-[20vw] font-black tabular-nums tracking-tighter leading-none mb-12 mt-8">{formatTime(timerSeconds)}</h1>
                <div className="flex flex-row gap-4">
                    <button onClick={() => setTimerRunning(!timerRunning)} className={`${btnLarge} ${timerRunning ? 'bg-yellow-600' : 'bg-green-600'}`}>{timerRunning ? <Pause size={40} /> : <Play size={40} fill="currentColor" className="ml-2" />}</button>
                    {!timerRunning && timerSeconds > 0 && (
                        <button onClick={() => { setRecordedTimes(prev => ({...prev, [activeTimerTask.name]: formatTime(timerSeconds)})); setActiveTimerTask(null); setTimerSeconds(0); }} className={`${btnLarge} bg-blue-600 hover:bg-blue-500`}><CheckCircle size={40} /></button>
                    )}
                </div>
                <button onClick={() => { setActiveTimerTask(null); setTimerRunning(false); }} className="mt-12 px-8 py-4 bg-zinc-800 text-xl font-bold rounded-lg hover:bg-zinc-700 transition-colors">Terug</button>
            </div>
        );
    }

    return (
        <div className={`min-h-screen flex flex-col items-center p-2 sm:p-4 transition-colors duration-500 ${restTime > 0 || intervalPhase === 'rest' ? 'bg-red-950' : 'bg-zinc-950'} text-white relative focus-mode-container overflow-y-auto pb-24`}>
            {isStructuredFitness && restTime > 0 && (
                <div onClick={() => setRestTime(0)} className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] bg-red-600 text-white font-black text-xl sm:text-2xl px-6 py-3 rounded-2xl shadow-[0_10px_40px_rgba(220,38,38,0.5)] animate-pulse flex items-center justify-center gap-3 cursor-pointer w-[90%] max-w-sm">
                    <Clock size={28} className="shrink-0" />
                    <span className="whitespace-nowrap tracking-wider">RUST: {formatTime(restTime)}</span>
                </div>
            )}
            <button onClick={toggleFullScreen} className="fixed top-4 right-4 p-3 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded-xl focus-hide z-[99998] shadow-2xl transition-colors">
                {isFullscreen ? <Minimize size={20} className="text-white" /> : <Maximize size={20} className="text-white" />}
            </button>

            {hr.isSupported && (
                <div className="w-full max-w-2xl mx-auto mt-2 mb-2 px-1 z-30">
                    {!hr.isConnected ? (
                        <div className="flex justify-center">
                            <div className="flex flex-wrap justify-center gap-2">
                            <button
                                type="button"
                                onClick={() => hr.connectHeartRate(false)}
                                disabled={hr.isConnecting}
                                className="px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-red-500/50 rounded-xl text-xs font-bold text-gray-300 hover:text-white flex items-center gap-2 transition-all shadow-sm"
                            >
                                <Heart size={14} className="text-red-500" />
                                <span>{hr.isConnecting ? 'Zoeken...' : 'Koppel HR Band'}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => hr.connectHeartRate(true)}
                                disabled={hr.isConnecting}
                                className="px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-blue-500/50 rounded-xl text-xs font-bold text-gray-300 hover:text-white flex items-center gap-2 transition-all shadow-sm"
                                title="Gebruik deze knop voor Garmin Fenix / Forerunner (zet eerst Zend hartslag uit aan op je horloge)"
                            >
                                <Bluetooth size={14} className="text-blue-500" />
                                <span>Zoek Horloge (Garmin/Alle)</span>
                            </button>
                        </div>
                        </div>
                    ) : (
                        <div className={`w-full ${hr.zoneInfo.bg} border ${hr.zoneInfo.border} rounded-2xl px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 shadow-lg transition-colors`}>
                            <div className="flex items-center gap-3">
                                <div className="relative flex items-center justify-center">
                                    <Heart size={24} className="text-red-500 fill-red-500 animate-pulse" />
                                </div>
                                <div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-2xl font-black text-white tabular-nums leading-none">{hr.currentBpm || '--'}</span>
                                        <span className="text-[10px] font-black text-gray-400 uppercase">BPM ({hr.zoneInfo.pct}%)</span>
                                    </div>
                                    <span className={`text-[11px] font-black uppercase tracking-wider ${hr.zoneInfo.color}`}>
                                        {hr.zoneInfo.name}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 text-right">
                                <div className="hidden sm:flex items-center gap-3 text-xs">
                                    <div>
                                        <span className="text-[9px] text-gray-400 uppercase block font-bold">Gem</span>
                                        <span className="font-black text-white tabular-nums">{hr.avgBpm}</span>
                                    </div>
                                    <div>
                                        <span className="text-[9px] text-gray-400 uppercase block font-bold">Max</span>
                                        <span className="font-black text-white tabular-nums">{hr.maxBpm}</span>
                                    </div>
                                    {hr.liveCadence && (
                                        <div>
                                            <span className="text-[9px] text-gray-400 uppercase block font-bold">Cadans</span>
                                            <span className="font-black text-cyan-500 tabular-nums">{hr.liveCadence} spm</span>
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={hr.disconnectHeartRate}
                                    className="text-[10px] font-bold text-gray-400 hover:text-white bg-black/40 px-2.5 py-1 rounded-lg border border-white/10"
                                    title={`Verbonden met ${hr.deviceName}`}
                                >
                                    Loskoppelen
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!isStructuredFitness && (
                <div className="mt-4 mb-4 text-center w-full">
                    <p className="text-blue-500 text-2xl font-bold tracking-widest uppercase mb-1">{displayFormat}</p>
                    {(restTime > 0 || intervalPhase === 'rest') && (
                        <div onClick={() => setRestTime(0)} className="bg-red-600 text-white font-black text-2xl px-8 py-3 rounded-2xl animate-pulse mb-4 inline-block shadow-2xl cursor-pointer">
                            RUST: {formatTime(restTime || time)} <span className="text-sm block opacity-70">(Tik om te negeren)</span>
                        </div>
                    )}
                    <h1 className="text-[25vw] sm:text-[20vw] font-black tabular-nums leading-none mb-6 timer-display">{formatTime(time)}</h1>
                </div>
            )}
            <TimerControls isStructuredFitness={isStructuredFitness} currentWod={currentWod} isRunning={isRunning} setIsRunning={setIsRunning} soundEnabled={soundEnabled} setSoundEnabled={setSoundEnabled} rounds={rounds} setRounds={setRounds} currentIntervalRound={currentIntervalRound} skipToNextPhase={skipToNextPhase} setTime={setTime} setRestTime={setRestTime} />
            {currentWod?.preWorkoutAdvice && (
                <div className="w-full max-w-2xl mx-auto mt-6 bg-indigo-950/20 border border-indigo-500/20 rounded-xl p-4 flex items-start gap-3 shadow-lg">
                    <Zap className="text-indigo-400 shrink-0 mt-0.5" size={20} />
                    <div>
                        <h4 className="text-indigo-400 font-bold text-xs uppercase tracking-widest mb-1">Coach Advies</h4>
                        <p className="text-indigo-200 text-sm leading-relaxed">{currentWod.preWorkoutAdvice}</p>
                    </div>
                </div>
            )}
            {isStructuredFitness ? (
                <div className="w-full max-w-2xl space-y-4 mb-6 flex-1 pt-2 mt-6">
                    {displayGroups?.Warmup?.length > 0 && (
                        <div className="bg-zinc-900 border border-zinc-700 border-l-4 border-l-yellow-500 rounded-r-xl rounded-l-sm p-3 sm:p-4 shadow-xl relative mb-4">
                            <h2 className="text-lg font-black text-yellow-500 tracking-widest uppercase mb-2">Warmup</h2>
                            {displayGroups.Warmup.map((ex, idx) => (
                                <div key={idx} className="mb-2">
                                    <div className="flex items-center justify-between gap-3 bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                                        <p className="text-gray-300 whitespace-pre-wrap leading-relaxed flex-1 text-sm font-bold">{ex.cleanName || ex.name}</p>
                                        <button onClick={() => toggleInfo(`warmup-${idx}`)} className="flex items-center gap-1 text-[10px] font-black text-yellow-500 hover:text-white bg-yellow-900/20 px-2 py-1.5 rounded border border-yellow-500/30 transition-colors uppercase tracking-wider shrink-0"><Info size={14} /> Tip</button>
                                    </div>
                                    {infoOpen[`warmup-${idx}`] && (
                                        <div className="bg-zinc-800/50 border border-yellow-500/40 p-3 rounded-lg mt-1 mb-2 text-sm text-gray-200 shadow-inner">
                                            <strong className="text-yellow-500 block mb-1 uppercase tracking-wider text-xs">💡 Coach Tip:</strong>{getExerciseTip(ex)}
                                        </div>
                                    )}
                                    {(ex.name.toLowerCase().includes('emom') || ex.name.toLowerCase().includes('minuten') || ex.name.toLowerCase().includes('amrap')) && (
                                        <button onClick={() => { setActiveTimerTask(ex); setTimerSeconds(0); setTimerRunning(true); }} className="mt-2 bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors text-sm"><Play size={16} fill="currentColor" /> Start Focus</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {Object.entries(displayGroups.Training || {}).map(([letter, exercises]) => {
                        const isSuperset = exercises.length > 1;
                        return (
                            <div key={letter} className="bg-zinc-900 border border-zinc-700 border-l-4 border-l-orange-500 rounded-r-xl rounded-l-sm p-3 sm:p-4 shadow-xl relative">
                                {isSuperset && (<div className="mb-3 pb-2 border-b border-zinc-700"><h2 className="text-lg font-black text-orange-500 tracking-widest uppercase">Blok {letter} (Superset)</h2></div>)}
                                {exercises.map((ex, idx) => {
                                    const trackingKey = ex.originalIdx !== undefined ? ex.originalIdx : ex.name;
                                    if (ignoredExercises.includes(trackingKey)) return null;

                                    const previousPR = loggedPRs.find(p => p.name === ex.cleanName || p.name === ex.name) || (prs && prs.find(p => p.exercise && p.exercise.toLowerCase() === (ex.cleanName || ex.name).toLowerCase()));

                                    const currentSets = setTracking[trackingKey] || [];
                                    let maxE1RM = 0;
                                    currentSets.forEach(s => {
                                        const w = parseFloat(s.weight) || 0; const r = parseInt(s.reps) || 0; const rpe = parseFloat(s.rpe);
                                        if (w > 0 && r > 0) {
                                            let effReps = r; if (!isNaN(rpe) && rpe > 0 && rpe <= 10) effReps = r + (10 - rpe);
                                            const e = Math.round(w * (1 + (effReps / 30))); if (e > maxE1RM) maxE1RM = e;
                                        }
                                    });

                                    const rawRestValue = ex.restTime !== undefined ? ex.restTime : (ex.rest !== undefined ? ex.rest : null);
                                    let maxRest = null; if (rawRestValue !== null && rawRestValue !== "") { maxRest = parseRestTime(rawRestValue); }
                                    if (isSuperset && idx !== exercises.length - 1) { maxRest = 0; }

                                    const stepAmount = ex.step || (ex.equipment === 'DB' || ex.name.toLowerCase().includes('dumbbell') ? 2 : 2.5);
                                    const activeUnit = unitPreferences[trackingKey] || 'KG'; const tipText = getExerciseTip(ex);
                                    const isLastExerciseInBlock = idx === exercises.length - 1;

                                    return (
                                        <div key={trackingKey} className={`${isSuperset && !isLastExerciseInBlock ? 'mb-4 pb-4 border-b border-dashed border-zinc-700' : ''}`}>
                                            <div className="flex flex-col gap-2 mb-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <h3 className="text-lg font-black text-white flex items-center flex-wrap gap-2 flex-1">
                                                        <span className="text-orange-500">{ex.displayCode || letter}</span> {ex.cleanName || ex.name}
                                                    </h3>
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <button onClick={() => removeExerciseCompletely(trackingKey)} className="text-gray-500 hover:text-red-500 transition-colors p-1.5 rounded-lg border border-transparent hover:border-red-500/30" title="Oefening Verwijderen"><Trash2 size={16} /></button>
                                                        <button onClick={() => setSwapModal({isOpen: true, idx: trackingKey, oldName: ex.cleanName || ex.name})} className="text-gray-500 hover:text-blue-500 transition-colors p-1.5 rounded-lg border border-transparent hover:border-blue-500/30" title="Wissel Oefening"><RefreshCw size={16} /></button>
                                                        <button onClick={() => toggleInfo(trackingKey)} className="text-gray-500 hover:text-purple-500 transition-colors p-1.5 rounded-lg border border-transparent hover:border-purple-500/30" title="Coach Tip"><Info size={16} /></button>
                                                        <button onClick={() => toggleRpe(trackingKey)} className={`transition-colors p-1.5 rounded-lg border ${rpeOpen[trackingKey] ? 'text-orange-500 border-orange-500/50 bg-orange-500/10' : 'text-gray-500 hover:text-orange-500 border-transparent'}`} title="RPE Invullen"><Zap size={16} fill={rpeOpen[trackingKey] ? 'currentColor' : 'none'} /></button>
                                                        <button onClick={() => toggleNote(trackingKey)} className={`transition-colors p-1.5 rounded-lg border ${exerciseNotes[trackingKey] && exerciseNotes[trackingKey].trim() !== '' ? 'text-orange-500 border-orange-500/50 bg-orange-500/10' : 'text-gray-500 hover:text-blue-500 border-transparent'}`} title="Notitie"><MessageSquare size={16} fill={exerciseNotes[trackingKey] && exerciseNotes[trackingKey].trim() !== '' ? 'currentColor' : 'none'} /></button>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-2 items-center text-[10px] font-black uppercase tracking-widest">
                                                    {ex.tempo && <span className="bg-zinc-800 text-purple-500 border border-zinc-700 px-2 py-1 rounded">T: {ex.tempo}</span>}
                                                    {ex.rpe && <span className="bg-zinc-800 text-red-500 border border-zinc-700 px-2 py-1 rounded">RPE: {ex.rpe}</span>}
                                                    <span className="text-gray-400 bg-zinc-800/50 px-2 py-1 rounded border border-zinc-700">Doel: {ex.isFailure ? `${ex.sets}x Tot Failure` : `${ex.sets}x${ex.reps}`}</span>
                                                    {previousPR && <span className="text-amber-500 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded flex items-center gap-1"><ShieldCheck size={12}/> PR: {previousPR.weight}{activeUnit.toLowerCase()}</span>}
                                                    {maxE1RM > 0 && <span className="text-blue-500 bg-blue-500/10 border border-blue-500/30 px-2 py-1 rounded flex items-center gap-1">E1RM: {maxE1RM} {activeUnit.toLowerCase()}</span>}
                                                </div>
                                            </div>
                                            {infoOpen[trackingKey] && (<div className="bg-zinc-800/50 border border-purple-500/40 p-3 rounded-lg mb-3 text-sm text-gray-200 shadow-inner"><strong className="text-purple-500 block mb-1 uppercase tracking-wider text-xs">💡 Coach Tip:</strong>{tipText}</div>)}
                                            {(noteOpen[trackingKey] || (exerciseNotes[trackingKey] && exerciseNotes[trackingKey].trim() !== '')) && (
                                                <div className="mb-3 p-3 bg-zinc-800/50 border-l-4 border-l-yellow-500 border border-zinc-700 rounded-r-lg rounded-l-sm relative">
                                                    <span className="text-yellow-500 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-1"><MessageSquare size={10}/> Notitie</span>
                                                    <textarea value={exerciseNotes[trackingKey] || ''} onChange={(e) => updateNote(trackingKey, e.target.value)} placeholder="Typ hier je notitie voor deze oefening..." className="w-full bg-black/40 border border-zinc-700 rounded p-2 text-sm text-white outline-none focus:border-orange-500 resize-none min-h-[60px]" />
                                                </div>
                                            )}
                                            <div className="grid grid-cols-[2.5rem_1fr_1fr_3rem] gap-2 px-1 mb-1 text-[10px] font-black text-gray-500 uppercase text-center tracking-widest">
                                                <div>Set</div><div>Reps</div><div className="flex items-center justify-center gap-1"><button onClick={() => toggleUnit(trackingKey)} className="text-blue-500 hover:text-orange-500 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/30">{activeUnit}</button></div><div>✓</div>
                                            </div>
                                            <div className="space-y-2 mb-1">
                                                {currentSets.map((set, sIdx) => {
                                                    const isLastSetInBlock = isLastExerciseInBlock && sIdx === currentSets.length - 1;
                                                    return (<SetRow key={`${ex.cleanName || ex.name}-${sIdx}`} set={set} sIdx={sIdx} trackingKey={trackingKey} stepAmount={stepAmount} maxRest={maxRest} cycleSetType={cycleSetType} updateSetData={updateSetData} setWeightModal={setWeightModal} toggleSet={(key, idx, rest) => toggleSet(key, idx, rest, isLastSetInBlock)} isBarbell={isBarbellExercise(ex.name || ex.cleanName)} showRpe={rpeOpen[trackingKey]} />);
                                                })}
                                            </div>
                                            <div className="flex justify-between items-center mt-2.5 px-1">
                                                <button onClick={() => addSet(trackingKey)} className="text-[10px] text-emerald-500 hover:text-emerald-400 font-black tracking-widest uppercase transition-colors px-2.5 py-1 bg-emerald-500/10 rounded-md border border-emerald-500/30">+ Extra set</button>
                                                <button onClick={() => removeLastSet(trackingKey)} className="text-[10px] text-red-500 hover:text-red-400 font-black tracking-widest uppercase transition-colors px-2.5 py-1 bg-red-500/10 rounded-md border border-red-500/30">- Wis set</button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                <div className="flex justify-center mt-2 mb-4 w-full px-2">
                    <button onClick={() => setAddExModal(true)} className="w-full sm:w-auto px-6 py-4 bg-zinc-900 border border-zinc-700 hover:border-orange-500 hover:text-orange-500 rounded-xl text-gray-300 font-bold transition-colors flex justify-center items-center gap-2 shadow-lg">
                        <Plus size={20} /> Extra Oefening Toevoegen
                    </button>
                </div>
                {displayGroups?.Cardio?.length > 0 && (
                    <div className="bg-zinc-900 border border-zinc-700 border-l-4 border-l-cyan-500 rounded-r-xl rounded-l-sm p-3 sm:p-4 shadow-xl relative mt-4 mb-4">
                        <h2 className="text-lg font-black text-cyan-500 tracking-widest uppercase mb-2">Cardio & Conditioning</h2>
                        {displayGroups.Cardio.map((ex, idx) => (
                            <div key={idx} className="mb-2">
                                <div className="flex items-center justify-between gap-3 bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                                    <div className="flex-1">
                                        <p className="text-white whitespace-pre-wrap leading-relaxed text-sm font-black">{ex.cleanName || ex.name}</p>
                                        {(ex.cardioTarget || ex.reps) && (
                                            <p className="text-xs text-cyan-500 font-bold mt-0.5">
                                                Doel: {ex.cardioTarget || `${ex.sets ? ex.sets + 'x ' : ''}${ex.reps}`}
                                            </p>
                                        )}
                                    </div>
                                    <button onClick={() => toggleInfo(`cardio-${idx}`)} className="text-gray-500 hover:text-cyan-500 transition-colors p-1.5 rounded-full border border-transparent shrink-0" title="Coach Tip"><Info size={16} /></button>
                                    <button onClick={() => toggleNote(`cardio-${idx}`)} className={`transition-colors p-1.5 rounded-full border shrink-0 ${exerciseNotes[`cardio-${idx}`] && exerciseNotes[`cardio-${idx}`].trim() !== '' ? 'text-orange-500 border-orange-500/50 bg-orange-500/10' : 'text-gray-500 hover:text-blue-500 border-transparent'}`} title="Notitie"><MessageSquare size={16} fill={exerciseNotes[`cardio-${idx}`] && exerciseNotes[`cardio-${idx}`].trim() !== '' ? 'currentColor' : 'none'} /></button>
                                </div>
                                {infoOpen[`cardio-${idx}`] && (<div className="bg-zinc-800/50 border border-cyan-500/40 p-3 rounded-lg mt-1 mb-2 text-sm text-gray-200 shadow-inner"><strong className="text-cyan-500 block mb-1 uppercase tracking-wider text-xs">💡 Coach Tip:</strong>{getExerciseTip(ex)}</div>)}
                                {(noteOpen[`cardio-${idx}`] || (exerciseNotes[`cardio-${idx}`] && exerciseNotes[`cardio-${idx}`].trim() !== '')) && (
                                    <div className="mt-1 mb-2 p-3 bg-zinc-800/50 border-l-4 border-l-yellow-500 border border-zinc-700 rounded-r-lg rounded-l-sm relative">
                                        <span className="text-yellow-500 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-1"><MessageSquare size={10}/> Notitie</span>
                                        <textarea value={exerciseNotes[`cardio-${idx}`] || ''} onChange={(e) => updateNote(`cardio-${idx}`, e.target.value)} placeholder="Typ hier je notitie..." className="w-full bg-black/40 border border-zinc-700 rounded p-2 text-sm text-white outline-none focus:border-orange-500 resize-none min-h-[60px]" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                </div>
            ) : (<p className="text-lg text-gray-300 mb-8 whitespace-pre-wrap text-center max-w-xl bg-zinc-900 p-6 rounded-xl border border-zinc-700 shadow-xl">{currentWod?.description}</p>)}

            <div className="w-full max-w-md mx-auto mt-6 pb-6 flex flex-col sm:flex-row gap-4 px-2">
                <button onClick={() => { const backTarget = (currentWod?.isRunSession || currentWod?.isRunReplay || String(currentWod?.type || '').startsWith('Run:')) ? 'running' : 'dashboard'; hr.disconnectHeartRate(); cleanupFocusMode(); setView(backTarget); }} className="flex-1 py-4 bg-zinc-800 text-lg font-bold rounded-xl hover:bg-zinc-700 border border-zinc-700 transition-all shadow-md">Terug</button>
                <button onClick={() => { const savedStartBtn = localStorage.getItem('focusStartedAt'); const el = Math.max(0, Math.round((Date.now() - (savedStartBtn ? parseInt(savedStartBtn) : new Date(focusStartedAt).getTime())) / 1000)); if ((time > 0 ? time : el) < 120) { setShowTimePrompt(true); } else { setShowConfirmModal(true); } }} className="flex-1 py-4 bg-green-600 text-white text-lg font-black rounded-xl hover:bg-green-500 shadow-[0_0_20px_rgba(22,163,74,0.3)] transition-all">Afronden</button>
            </div>

            {showTimePrompt && (
                <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex justify-center items-center p-4">
                    <div className="bg-zinc-900 p-6 sm:p-8 rounded-2xl w-full max-w-md border border-zinc-700 shadow-2xl">
                        <h3 className="text-xl font-black text-orange-500 mb-2">Achteraf Gelogd?</h3>
                        <p className="text-sm text-gray-400 mb-4">
                            Je rondt deze sessie binnen 2 minuten af. Vul de daadwerkelijke trainingsduur (in minuten) in. Je kunt hierna nog elk gewicht en elke set vrij aanpassen!
                        </p>
                        <input type="number" value={timeInput} onChange={(e) => setTimeInput(e.target.value)} className="w-full bg-black/50 border border-zinc-600 rounded-xl p-4 text-white text-2xl font-black text-center mb-5 outline-none focus:border-orange-500" placeholder="Minuten..." />
                        <div className="flex flex-col gap-2.5">
                            <button
                                onClick={() => {
                                    setManualDuration(parseInt(timeInput || 0, 10) * 60);
                                    markAllSetsCompleted();
                                    setShowTimePrompt(false);
                                    setShowConfirmModal(true);
                                }}
                                className="w-full py-3.5 bg-green-600 hover:bg-green-500 text-white font-black rounded-xl uppercase tracking-wider text-sm"
                            >
                                ✅ Alle Sets Voltooid & Direct Opslaan
                            </button>
                            <button
                                onClick={() => {
                                    setManualDuration(parseInt(timeInput || 0, 10) * 60);
                                    markAllSetsCompleted();
                                    setShowTimePrompt(false);
                                    showToast('Tijd ingesteld! Tik op een gewicht om het aan te passen en klik daarna op Afronden.', 'info');
                                }}
                                className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl text-xs sm:text-sm"
                            >
                                ✏️ Eerst nog gewichten / reps aanpassen
                            </button>
                            <button
                                onClick={() => setShowTimePrompt(false)}
                                className="w-full py-2.5 bg-zinc-800 text-gray-400 font-bold rounded-xl text-xs"
                            >
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {addExModal && (
                <div className="fixed inset-0 z-[99999] bg-black/80 flex items-center justify-center p-4">
                    <div className="bg-zinc-900 border border-zinc-700 p-6 rounded-3xl w-full max-w-lg shadow-2xl flex flex-col max-h-[85vh]">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-black text-orange-500 flex items-center gap-2"><Plus size={24}/> Oefening Toevoegen</h3>
                            <button onClick={() => {setAddExModal(false); setAddExSearch('');}} className="text-gray-500 hover:text-white transition-colors p-2 bg-zinc-800 rounded-full"><X size={20}/></button>
                        </div>
                        <div className="relative mb-2 shrink-0">
                            <Search className="absolute left-4 top-3.5 text-gray-500" size={20} />
                            <input type="text" placeholder="Zoek oefening..." value={addExSearch} onChange={(e) => setAddExSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white focus:border-orange-500 outline-none transition-colors" autoFocus />
                        </div>
                        <div className="overflow-y-auto flex-1 space-y-2 pr-2 custom-scrollbar mt-2">
                            {(() => {
                                const seen = new Set();
                                const combined = [];
                                if (Array.isArray(exerciseDatabase)) {
                                    exerciseDatabase.forEach(e => {
                                        const n = String(e.exercise_name || e.name || e.title || "").trim();
                                        if (n && !seen.has(n.toLowerCase())) {
                                            seen.add(n.toLowerCase());
                                            combined.push({ title: n, desc: e.tip || e.description || e.desc || "", keyword: e.primary_muscles || e.muscleGroup || "" });
                                        }
                                    });
                                }
                                if (Array.isArray(LOCAL_EXERCISES)) {
                                    LOCAL_EXERCISES.forEach(e => {
                                        const n = String(e.title || e.name || "").trim();
                                        if (n && !seen.has(n.toLowerCase())) {
                                            seen.add(n.toLowerCase());
                                            combined.push({ title: n, desc: e.desc || e.tip || "", keyword: e.keyword || "" });
                                        }
                                    });
                                }
                                return combined
                                    .sort((a, b) => a.title.localeCompare(b.title))
                                    .filter(e => (e.title + " " + e.keyword + " " + e.desc).toLowerCase().includes(addExSearch.toLowerCase()));
                            })().map((ex, i) => (
                                <button key={i} onClick={() => handleAddCustomExercise(ex.title || ex.name)} className="w-full text-left p-4 bg-zinc-800/50 hover:bg-orange-500/10 rounded-xl border border-zinc-700 hover:border-orange-500/50 transition-all">
                                    <div className="font-bold text-orange-500">{typeof ex.title === "string" ? ex.title : (typeof ex.name === "string" ? ex.name : "Oefening")}</div>
                                    {((typeof ex.desc === "string" && ex.desc) || (typeof ex.tip === "string" && ex.tip)) && <div className="text-xs text-gray-400 mt-1 line-clamp-2">{typeof ex.desc === "string" && ex.desc ? ex.desc : ex.tip}</div>}
                                </button>
                            ))}
                            {addExSearch.trim() !== '' && !LOCAL_EXERCISES.find(e => ((e.title || e.name || '')).toLowerCase() === addExSearch.trim().toLowerCase()) && (
                                <button onClick={() => handleAddCustomExercise(addExSearch.trim())} className="w-full text-left p-4 bg-orange-600/20 hover:bg-orange-600/40 rounded-xl border border-orange-500/30 transition-all mt-4 flex items-center justify-between group">
                                    <div>
                                        <div className="font-bold text-orange-500">"{addExSearch.trim()}" Toevoegen</div>
                                        <div className="text-xs text-gray-400 mt-1">Oefening onbekend. Deze wordt direct als extra set aangemaakt.</div>
                                    </div>
                                    <Plus className="text-orange-500 group-hover:scale-110 transition-transform" size={24} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <WorkoutModals weightModal={weightModal} setWeightModal={setWeightModal} updateSetData={updateSetData} showConfirmModal={showConfirmModal} setShowConfirmModal={setShowConfirmModal} logWorkoutWithDetails={logWorkoutWithDetails} />
        </div>
    );
}
